import { Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import Breadcrumbs from 'components/breadcrumbs';
import FlexBox from 'components/flex-box';
import Paper from 'components/paper';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import ToggleButton from 'components/toggle-button';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { OwnerType } from 'types/shared/owner-type.enum';
import { ProfileSubType, ProfileType } from 'types/shared/profile-type.enum';
import _ from 'underscore';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import ProfileGrid from 'views/profiles-strategies/components/profiles-data-grid';
import { PeriodizationType } from 'views/profiles-strategies/features/profiles/types/periodization-type.enum';
import { UpdateProfileInput } from 'views/profiles-strategies/features/profiles/types/update-profile-input.type';
import ProfilesStrategiesTabHeader from '../../../components/profile-strategies-tabs-header';
import { postProfileByType } from '../api/create-profile';

type Props = {};

type Row = {
  id: string;
  period: number;
  average: number;
  rowIndex: number;
};

const CreateProfiles: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const [rows, setRows] = useState<GridRowsProp<Row>>([]);
  const { errorDictionary, changeErrorDictionary, handleEditing, isEditing, hasError } = useDataGridError();

  const PAGE_NAME_ID = 'profilesStrategies.createProfiles';

  const getOwnerTypeId = () => {
    switch (ownerType) {
      case OwnerType.MARKET:
        return account.company?.marketId;
      case OwnerType.COMPANY:
        return account.company?.companyId;
      case OwnerType.SITE:
        return siteId;
    }
  };

  // #region STATE

  const [profileType, setProfileType] = useState<ProfileType>(ProfileType.EGI);
  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.SITE);
  const [siteId, setSiteId] = useState(_.first(account.company!.addressAccess.filter((site) => site.seaCloudId))?.id);

  // #endregion STATE

  // #region FORMS

  const { handleSubmit, control, setValue, watch } = useForm<UpdateProfileInput>({
    defaultValues: {
      name: '',
      active: false,
      circular: false,
      fromInput: false,
      isTemporary: false,
      modelSubType: ProfileSubType.EGI,
      periodisation: PeriodizationType.WEEK,
      lines: [{ period: 1, average: 0 }],
    },
    shouldUnregister: false,
  });

  // #region POST DATA
  const createProfile = usePost<number, UpdateProfileInput>(
    (request) => postProfileByType(profileType, ownerType, String(getOwnerTypeId()), request),
    (response) => {
      history.push(`/profiles-strategies/profiles/${profileType}/${response}`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleUpdateProfile = (request: UpdateProfileInput) => {
    let data = {
      ...request,
      lines: rows.map((line) => {
        return {
          period: line.period,
          average: line.average,
        };
      }),
    };
    createProfile.mutate(data);
  };

  const getProfileSubTypes = (type: ProfileType): ProfileSubType[] => {
    switch (type) {
      case ProfileType.EGI:
        return [ProfileSubType.EGI];
      case ProfileType.EFI:
        return [ProfileSubType.FCR, ProfileSubType.EFI];
      case ProfileType.MORTALITY:
        return [ProfileSubType.MORTALITY];
      default:
        return [];
    }
  };

  const getOwnerTypes = () => {
    let ownerTypes = [];

    if (account.company?.features.hasPlanningPrognosisManageMarketProfiles)
      ownerTypes.push({ label: 'market', value: OwnerType.MARKET });

    if (account.company?.features.hasPlanningPrognosisManageCompanyOrSiteProfiles) {
      ownerTypes.push({ label: 'company', value: OwnerType.COMPANY });
      ownerTypes.push({ label: 'site', value: OwnerType.SITE });
    }

    return ownerTypes;
  };

  return (
    <>
      <ProfilesStrategiesTabHeader utils={<Breadcrumbs />} />

      <form onSubmit={handleSubmit(handleUpdateProfile)}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography pb={4} variant="h2" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.generalTitle`} />
              </Typography>
              <Paper>
                <FlexBox>
                  <TextField
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `profilesStrategies.label.name` })}
                    controller={{
                      name: 'name',
                      control: control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <Grid>
                    <ToggleButton
                      dark
                      label={intl.formatMessage({ id: `profilesStrategies.editProfiles.profileToggle.active` })}
                      controller={{ name: 'active', control: control }}
                    />
                    <ToggleButton
                      dark
                      label={intl.formatMessage({ id: `profilesStrategies.editProfiles.profileToggle.temporary` })}
                      controller={{ name: 'isTemporary', control: control }}
                    />
                  </Grid>
                  <Grid>
                    <ToggleButton
                      dark
                      label={intl.formatMessage({ id: `profilesStrategies.editProfiles.profileToggle.circular` })}
                      controller={{ name: 'circular', control: control }}
                    />
                    <ToggleButton
                      dark
                      label={intl.formatMessage({ id: `profilesStrategies.editProfiles.profileToggle.input` })}
                      controller={{ name: 'fromInput', control: control }}
                    />
                  </Grid>
                </FlexBox>
              </Paper>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography pb={4} variant="h2" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.ownerTitle`} />
              </Typography>
              <Paper>
                <FlexBox>
                  <Select
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `profilesStrategies.label.profileType` })}
                    value={profileType}
                    onChange={(event) => {
                      setProfileType(event.target.value as ProfileType);
                      setValue('modelSubType', getProfileSubTypes(event.target.value as ProfileType)[0]);
                    }}>
                    <RadioSelectItem value={ProfileType.EGI} selected={profileType === ProfileType.EGI}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.profiles.egi`} />
                    </RadioSelectItem>
                    <RadioSelectItem value={ProfileType.EFI} selected={profileType === ProfileType.EFI}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.profiles.efi`} />
                    </RadioSelectItem>
                    <RadioSelectItem value={ProfileType.MORTALITY} selected={profileType === ProfileType.MORTALITY}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.profiles.mort`} />
                    </RadioSelectItem>
                  </Select>
                  {getProfileSubTypes(profileType).length > 1 && (
                    <Select
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `profilesStrategies.label.profileSubType` })}
                      controller={{
                        name: 'modelSubType',
                        control: control,
                      }}>
                      {getProfileSubTypes(profileType).map((type) => (
                        <RadioSelectItem key={type} value={type} selected={type === watch('modelSubType')}>
                          {type}
                        </RadioSelectItem>
                      ))}
                    </Select>
                  )}
                  <Select
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `profilesStrategies.ownerTitle` })}
                    value={ownerType}
                    onChange={(event) => setOwnerType(event.target.value as OwnerType)}>
                    {getOwnerTypes().map((owner) => (
                      <RadioSelectItem
                        value={owner.value}
                        key={`select-owner-type-${owner.value}`}
                        selected={owner.value === ownerType}>
                        <FormattedMessage id={`profilesStrategies.menuItem.${owner.label}`} />
                      </RadioSelectItem>
                    ))}
                  </Select>
                  {ownerType === OwnerType.SITE && (
                    <Select
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `profilesStrategies.menuItem.site` })}
                      value={siteId}
                      onChange={(event) => setSiteId(String(event.target.value))}>
                      {_.chain(account.company?.addressAccess)
                        .sortBy((s) => s.name.toLowerCase())
                        .value()
                        .map((site) => (
                          <RadioSelectItem key={site.id} value={site.id} selected={siteId === site.id}>
                            {site.name}
                          </RadioSelectItem>
                        ))}
                    </Select>
                  )}
                  <Select
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `profilesStrategies.label.periodisation` })}
                    controller={{
                      name: 'periodisation',
                      control: control,
                    }}>
                    <RadioSelectItem value={PeriodizationType.DAY} selected={PeriodizationType.DAY === watch('periodisation')}>
                      <FormattedMessage id={`general.day`} />
                    </RadioSelectItem>
                    <RadioSelectItem value={PeriodizationType.WEEK} selected={PeriodizationType.WEEK === watch('periodisation')}>
                      <FormattedMessage id={`general.week`} />
                    </RadioSelectItem>
                    <RadioSelectItem value={PeriodizationType.MONTH} selected={PeriodizationType.MONTH === watch('periodisation')}>
                      <FormattedMessage id={`general.month`} />
                    </RadioSelectItem>
                  </Select>
                </FlexBox>
              </Paper>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper transparent>
              <Typography pb={4} variant="h2" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.linesTitle`} />
              </Typography>
              <Box>
                <ProfileGrid
                  rows={rows}
                  setRows={setRows}
                  handleEditing={handleEditing}
                  errorDictionary={errorDictionary}
                  changeErrorDictionary={changeErrorDictionary}
                />
              </Box>
            </Paper>
          </Grid>
          <Box display="flex" justifyContent="end" pt={4} width={'100%'}>
            <Button type="submit" disabled={createProfile.isPending || isEditing || hasError}>
              {createProfile.isPending ? (
                <CircularProgress color="inherit" />
              ) : (
                <FormattedMessage id="general.submit" values={{ type: '' }} />
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );
};

export default CreateProfiles;
